/**
 * The "Content pack" billing card (ADR-021 §11): what this plugin's content
 * cost this month in storage and bandwidth, and the daily bandwidth alert.
 *
 * Bandwidth is shown by CHANNEL — host reads (cached on the viewer's device)
 * versus direct URLs (media streamed straight from the CDN) — so a contributor
 * can tell whether lesson text or streaming media is driving the bill.
 *
 * Recent days are an ESTIMATE until final: the ledger waits for CloudFront's
 * logs to be complete (~3 days) before a day is charged, and the card says so
 * rather than showing a number that may still move.
 *
 * Renders nothing for someone who is not an owner (the endpoint 403s) and for
 * a plugin with no content activity this month.
 */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { ContentPackBillingResponse } from '@/lib/api';
import {
  useContentPackBilling,
  useSetContentPackAlertThreshold,
  useSetContentPackHardCap,
} from '../hooks/useContentPackBilling';

const GIB = 1024 ** 3;

function formatBytes(bytes: number): string {
  if (bytes >= GIB) return `${(bytes / GIB).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatCredits(credits: number): string {
  return credits < 0.01 && credits > 0 ? '< 0.01' : credits.toFixed(2);
}

type Channels = ContentPackBillingResponse['summary']['egressBytes'];
const sum = (c: Channels) => c.read + c.url + c.other;

function hasActivity(s: ContentPackBillingResponse['summary']): boolean {
  return (
    s.storageCredits > 0 ||
    s.egressCredits > 0 ||
    sum(s.egressBytes) > 0 ||
    sum(s.provisionalEgressBytes) > 0 ||
    s.failedDays > 0 ||
    s.heldDays > 0 ||
    s.hardCapBytes !== null ||
    s.throttledToday ||
    s.publishingSuspendedAt !== null
  );
}

export function ContentPackBillingSection({ archeId }: { archeId: string }) {
  const { data } = useContentPackBilling(archeId);
  if (!data || !hasActivity(data.summary)) return null;
  return <ContentPackBillingCard archeId={archeId} data={data} />;
}

function ChannelRow({ label, hint, bytes }: { label: string; hint: string; bytes: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span>
        {label} <span className="text-muted-foreground">— {hint}</span>
      </span>
      <span className="font-mono tabular-nums">{formatBytes(bytes)}</span>
    </div>
  );
}

function ContentPackBillingCard({
  archeId,
  data,
}: {
  archeId: string;
  data: ContentPackBillingResponse;
}) {
  const { summary, rates, canEdit } = data;
  const total = summary.storageCredits + summary.egressCredits;
  const provisional = sum(summary.provisionalEgressBytes);

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Content pack</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {summary.publishingSuspendedAt && (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span>
              New content uploads are paused: this plugin&apos;s content charges failed for
              insufficient credits. Existing content keeps serving. Add credits and uploads resume
              after the next daily charge.
            </span>
          </div>
        )}

        {summary.throttledToday && (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span>
              Today&apos;s bandwidth reached your daily cap, so this plugin&apos;s content is paused
              until 00:00 UTC. Raise or remove the cap below to resume it now.
            </span>
          </div>
        )}

        <div>
          <div className="text-sm font-medium">This month (final days)</div>
          <div className="text-sm text-muted-foreground">
            <span className="font-mono tabular-nums">{formatCredits(total)}</span> credits — storage{' '}
            <span className="font-mono tabular-nums">{formatCredits(summary.storageCredits)}</span>,
            bandwidth{' '}
            <span className="font-mono tabular-nums">{formatCredits(summary.egressCredits)}</span> ·{' '}
            {summary.chargedDays} {summary.chargedDays === 1 ? 'day' : 'days'} charged
            {summary.failedDays > 0 && ` · ${summary.failedDays} failed`}
            {summary.heldDays > 0 && ` · ${summary.heldDays} under review`}
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-sm font-medium">Bandwidth by channel</div>
          <ChannelRow
            label="Host reads"
            hint="text and data read through useFiasContent, cached on the device"
            bytes={summary.egressBytes.read}
          />
          <ChannelRow
            label="Direct URLs"
            hint="media loaded straight from the CDN"
            bytes={summary.egressBytes.url}
          />
          {summary.egressBytes.other > 0 && (
            <ChannelRow
              label="Other"
              hint="manifests and untagged requests"
              bytes={summary.egressBytes.other}
            />
          )}
          {provisional > 0 && (
            <p className="pt-1 text-xs text-muted-foreground">
              Plus about {formatBytes(provisional)} in the last few days — an estimate until the
              CDN&apos;s logs are complete (~{Math.round(rates.finalizeAfterHours / 24)} days), then
              charged.
            </p>
          )}
        </div>

        <AlertThresholdControl
          archeId={archeId}
          thresholdBytes={summary.alertThresholdBytes}
          canEdit={canEdit}
        />

        <HardCapControl archeId={archeId} capBytes={summary.hardCapBytes} canEdit={canEdit} />

        <p className="text-xs text-muted-foreground">
          Rates: {rates.storageCreditsPerGbMonth} credits per GB-month stored,{' '}
          {rates.egressCreditsPerGb} credits per GB delivered. Replaced content is kept{' '}
          {rates.retentionDays} days after a new version goes live, then deleted — older versions
          can&apos;t be restored.
        </p>
      </CardContent>
    </Card>
  );
}

function AlertThresholdControl({
  archeId,
  thresholdBytes,
  canEdit,
}: {
  archeId: string;
  thresholdBytes: number;
  canEdit: boolean;
}) {
  const [draftGb, setDraftGb] = useState(String(Math.round((thresholdBytes / GIB) * 100) / 100));
  const mutation = useSetContentPackAlertThreshold(archeId);
  const parsed = Number(draftGb);
  const valid =
    draftGb.trim() !== '' && Number.isFinite(parsed) && parsed >= 0.01 && parsed <= 1_000_000;

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">Daily bandwidth alert</div>
      <p className="text-xs text-muted-foreground">
        You&apos;re notified once a day when this plugin&apos;s content passes this much bandwidth.
        It&apos;s an alert, not a cap — content keeps serving.
      </p>
      {canEdit ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) mutation.mutate(Math.round(parsed * GIB));
          }}
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              aria-label="Alert threshold in GB per day"
              className="w-28 rounded-md border bg-background px-2 py-1 font-mono text-sm tabular-nums"
              inputMode="decimal"
              value={draftGb}
              onChange={(e) => setDraftGb(e.target.value)}
            />
            GB / day
          </label>
          <Button type="submit" size="sm" disabled={!valid || mutation.isPending}>
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate(null, {
                onSuccess: (data) =>
                  setDraftGb(
                    String(Math.round((data.summary.alertThresholdBytes / GIB) * 100) / 100),
                  ),
              })
            }
          >
            Reset to default
          </Button>
          {mutation.isError && (
            <span className="text-sm text-destructive">Could not save the alert.</span>
          )}
        </form>
      ) : (
        <div className="text-sm font-mono tabular-nums">{formatBytes(thresholdBytes)} / day</div>
      )}
    </div>
  );
}

function HardCapControl({
  archeId,
  capBytes,
  canEdit,
}: {
  archeId: string;
  capBytes: number | null;
  canEdit: boolean;
}) {
  const [draftGb, setDraftGb] = useState(
    capBytes === null ? '' : String(Math.round((capBytes / GIB) * 100) / 100),
  );
  const mutation = useSetContentPackHardCap(archeId);
  const parsed = Number(draftGb);
  const valid =
    draftGb.trim() !== '' && Number.isFinite(parsed) && parsed >= 0.01 && parsed <= 1_000_000;

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">Daily hard cap (optional)</div>
      <p className="text-xs text-muted-foreground">
        Off by default. When on, this plugin&apos;s content stops serving for the rest of the day
        (until 00:00 UTC) once it passes the cap — the plugin breaks for its users until then. The
        cap takes effect as the CDN&apos;s logs arrive, so a day can run somewhat past it.
      </p>
      {canEdit ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) mutation.mutate(Math.round(parsed * GIB));
          }}
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              aria-label="Hard cap in GB per day"
              className="w-28 rounded-md border bg-background px-2 py-1 font-mono text-sm tabular-nums"
              inputMode="decimal"
              placeholder="off"
              value={draftGb}
              onChange={(e) => setDraftGb(e.target.value)}
            />
            GB / day
          </label>
          <Button type="submit" size="sm" disabled={!valid || mutation.isPending}>
            {capBytes === null ? 'Turn on' : 'Save'}
          </Button>
          {capBytes !== null && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(null, { onSuccess: () => setDraftGb('') })}
            >
              Turn off
            </Button>
          )}
          {mutation.isError && (
            <span className="text-sm text-destructive">Could not save the cap.</span>
          )}
        </form>
      ) : (
        <div className="text-sm font-mono tabular-nums">
          {capBytes === null ? 'Off' : `${formatBytes(capBytes)} / day`}
        </div>
      )}
    </div>
  );
}
